import { type CSSProperties, useEffect, useRef } from "react";
import { verticalCards } from "../constants";

export default function HorizontalScroll() {
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    let scrollVelocity = 0;
    let animationFrame = 0;

    const stopScrollLoop = () => {
      if (!animationFrame) return;
      cancelAnimationFrame(animationFrame);
      animationFrame = 0;
    };

    const smoothScroll = () => {
      if (scrollVelocity === 0) {
        animationFrame = 0;
        return;
      }

      container.scrollLeft += scrollVelocity;
      animationFrame = requestAnimationFrame(smoothScroll);
    };

    const startScrollLoop = () => {
      if (!animationFrame) {
        animationFrame = requestAnimationFrame(smoothScroll);
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      const { left, width } = container.getBoundingClientRect();
      const mouseX = e.clientX - left; // relative to container
      const center = width / 2;

      const maxSpeed = 15; // max scroll speed
      const deadZone = 20; // px area around center that doesn’t scroll

      const delta = mouseX - center;

      if (Math.abs(delta) > deadZone) {
        const intensity =
          (delta - Math.sign(delta) * deadZone) / (width / 2 - deadZone);
        scrollVelocity = maxSpeed * intensity;
        startScrollLoop();
      } else {
        scrollVelocity = 0;
        stopScrollLoop();
      }
    };

    const handleMouseLeave = () => {
      scrollVelocity = 0;
      stopScrollLoop();
      // Snap back to center smoothly
      const middleCardIndex = Math.floor(verticalCards.length / 2 - 1);
      const middleCard = container.children[middleCardIndex] as HTMLElement;
      if (middleCard) {
        const scrollTo =
          middleCard.offsetLeft +
          middleCard.offsetWidth / 2 -
          container.clientWidth / 2;

        container.scrollTo({
          left: scrollTo,
          behavior: "smooth",
        });
      }
    };

    // Attach only inside container
    container.addEventListener("mousemove", handleMouseMove);
    container.addEventListener("mouseleave", handleMouseLeave);

    // Center on mount
    handleMouseLeave();

    return () => {
      container.removeEventListener("mousemove", handleMouseMove);
      container.removeEventListener("mouseleave", handleMouseLeave);
      stopScrollLoop();
    };
  }, []);

  const handleSmoothScroll = (e: React.MouseEvent<HTMLAnchorElement>) => {
    const target = e.currentTarget.getAttribute("href");
    if (!target) return;

    // Only intercept in-page anchors
    if (target.startsWith("#")) {
      e.preventDefault();
      const element = document.querySelector(target);
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    }
  };

  return (
    <div className="w-screen md:h-screen overflow-hidden" id="services">
      {/* Desktop Horizontal Scroll */}
      <div
        ref={containerRef}
        className="hidden md:flex overflow-x-scroll no-scrollbar w-full h-full"
      >
        {verticalCards.map((card, i) => (
          <a
            key={i}
            href={card.href}
            onClick={(e) => {
              handleSmoothScroll(e);
            }}
            className="group card flex-shrink-0 w-[500px] h-full flex flex-col items-start justify-center rounded-none shadow-none p-10 cursor-pointer transition-all duration-500 ease-in-out bg-[#0D1821] text-[#c3f620] hover:bg-[var(--card-bg)] hover:text-[var(--card-text)] hover:scale-[1.03]"
            style={
              {
                "--card-bg": card.bgColor,
                "--card-text": card.textColor,
              } as CSSProperties
            }
          >
            <h2 className="text-4xl mb-4">{card.title}</h2>

            {/* Show only on hover (desktop) */}
            <p className="hidden group-hover:block opacity-0 group-hover:opacity-100 transition-opacity duration-300 mb-6 max-w-md font-poppins text-left ">
              {card.desc}
            </p>
          </a>
        ))}
      </div>

      {/* Mobile: full-width stacked with dividers */}
      <div className="flex flex-col md:hidden w-full h-full overflow-y-scroll no-scrollbar mt-14">
        {verticalCards.map((card, i) => (
          <a
            key={i}
            href={card.href}
            onClick={(e) => {
              handleSmoothScroll(e);
            }}
            className="w-full px-8 pt-6 cursor-pointer bg-[#F5F5F5]"
          >
            <h2 className="text-lg font-bold mb-2 text-[#0d1821]">
              {card.title}
            </h2>
            <p className="text-sm text-[#0d1821] mb-4 font-poppins text-left">
              {card.desc}
            </p>
            <button className="text-bold w-10 h-10 flex items-center justify-center rounded-sm bg-[#c3f620] text-[#0d1821]">
              →
            </button>
            <div className="pt-6 border-b border-[#0d1821]/30" />
          </a>
        ))}
      </div>
    </div>
  );
}
